import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import * as signalR from '@microsoft/signalr';
import { BehaviorSubject } from 'rxjs';
import { IBanner } from '@core/interfaces/ibanner';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class BannerSocketService {
  private connection?: signalR.HubConnection;
  private state$ = new BehaviorSubject<IBanner>({ message: '', isActive: false });
  banner$ = this.state$.asObservable();
  private isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

  private get hubUrl(): string {
    const base = (environment.apiUrl || '').replace(/\/$/, '');
    return `${base}/hubs/banner`;
  }

  start(): void {
    if (!this.isBrowser) {
      console.info('[BannerSocket] Not starting (SSR / non-browser).');
      return;
    }
    if (this.connection) {
      console.info('[BannerSocket] Already started.');
      return;
    }

    const hubUrl = this.hubUrl;
    console.info('[BannerSocket] Starting…', { hubUrl });

    this.connection = new signalR.HubConnectionBuilder()
      .withUrl(hubUrl, {
        withCredentials: true
      })
      .configureLogging(signalR.LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    this.connection.on('BannerUpdated', (payload: IBanner) => {
      console.info('[BannerSocket] BannerUpdated', payload);
      this.pushState(payload);
    });

    this.connection.onreconnecting(err => {
      console.warn('[BannerSocket] Reconnecting…', err);
    });

    this.connection.onreconnected(id => {
      console.info('[BannerSocket] Reconnected', { connectionId: id });
    });

    this.connection.onclose(err => {
      console.warn('[BannerSocket] Closed', err);
    });

    this.connection
      .start()
      .then(() => {
        console.info('[BannerSocket] Started', { connectionId: this.connection?.connectionId });
      })
      .catch(err => {
        console.error('[BannerSocket] SignalR start error', err);
      });
  }

  stop(): void {
    if (!this.connection) return;
    console.info('[BannerSocket] Stopping…');
    this.connection.stop()
      .then(() => console.info('[BannerSocket] Stopped'))
      .catch(err => console.warn('[BannerSocket] Stop error', err));
    this.connection = undefined;
  }

  /** Seed initial value from REST GET /banner */
  seed(current: IBanner) {
    console.info('[BannerSocket] Seed', current);
    this.pushState(current);
  }

  private pushState(src?: IBanner) {
    this.state$.next({
      message: src?.message?.trim() ?? '',
      isActive: !!src?.isActive
    });
  }
}
